import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, isDevMode, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseHorizontalNavigationComponent, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { OutstandingActionsComponent } from 'app/layout/common/outstanding-actions/outstanding-actions.component';
import { SettingThemeService } from 'app/shared/services/setting-theme.service';
import { SettingTheme } from 'app/shared/types/setting-theme-type';

@Component({
    selector: 'dante-layout',
    templateUrl: './dante.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        UserComponent,
        OutstandingActionsComponent,
        FuseHorizontalNavigationComponent,
        RouterOutlet,
        CommonModule,
        RouterLink
    ]
})

export class DanteLayoutComponent implements OnInit {
    headerScrolls = false;
    isScreenSmall: boolean = false;
    navigation?: Navigation;
    settingTheme?: SettingTheme;

    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _themeService: SettingThemeService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _destroyRef: DestroyRef,
        private _router: Router,
        private _activatedRoute: ActivatedRoute
    ) { }

    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        this._updateShellLayoutFromRoute();

        this._router.events
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this._updateShellLayoutFromRoute();
                }
            });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this._themeService.getSettingTheme()
            .subscribe((settingTheme: SettingTheme) => {
                this.settingTheme = settingTheme;
                this.settingTheme.logo = '/content/images/' + settingTheme.logo;
                if (isDevMode()) {
                    this.settingTheme.logo = 'https://andrew.miniplanner.dev' + this.settingTheme.logo;
                }
                this._changeDetectorRef.detectChanges();
            });
    }

    toggleNavigation(name: string): void {
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            navigation.toggle();
        }
    }

    private _updateShellLayoutFromRoute(): void {
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        this.headerScrolls = route.pathFromRoot.some(
            (path) => path.routeConfig?.data?.headerScrolls === true
        );
    }
}
